/* =========================================================================
 * DID ANYBODY ACTUALLY LOOK? — the hunt's own account of itself.
 *
 * Brian: "when a championship cant be found - i click find champion - nothing
 * is found and then it pushes me to a page to input my own champion. it never
 * says it searched."
 *
 * ⚠️ THE DEFECT, AND IT IS THE SAME ONE AS THE SEARCH BOX.
 *
 * Pressing FIND opened a form. The record already carried three fields saying
 * what had been tried — `sr_champion_search`, `champion_searched_at`,
 * `champion_search_attempts` — and the workspace rendered NONE of them. So a
 * record searched six times and a record nobody has ever looked at presented
 * IDENTICALLY: a blank box asking the rep to type a name.
 *
 * That is the silent-zero failure again, in a different surface. "We looked
 * hard and no source names a winner" is a finding worth acting on — it usually
 * means the event does not publish results, which is a fact about the event.
 * "Nobody has looked yet" is a queue position. Rendering both as an empty form
 * teaches a rep the system does nothing, which is exactly the conclusion Brian
 * drew.
 *
 * ⚠️ AND IT MUST NOT OVERSTATE. Nothing in this browser can search the web —
 * the metered control was deleted by policy (huntJob.js). The searching is done
 * by the hand hunt / the nightly agent on the subscription. So this states what
 * the RECORD says was done, never "I just searched for you". A panel that
 * claimed to have searched when it only opened a form would be a worse lie
 * than the silence it replaces.
 *
 * PURE, no imports — verify-hunt.mjs drives it directly under node.
 * ========================================================================= */

const S = (v) => (v == null ? '' : String(v)).trim()
const DAY = 86400000

/** "today" / "yesterday" / "6 days ago" / "on 2026-04-02" — never a raw ISO blob. */
export function whenPhrase(iso, now = new Date()) {
  const t = Date.parse(S(iso))
  if (!Number.isFinite(t)) return ''
  const days = Math.floor((now.getTime() - t) / DAY)
  if (days < 0) return 'just now'
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  return `on ${new Date(t).toISOString().slice(0, 10)}`
}

/**
 * searchHistory(record, now) → { key, headline, detail, tone, attempts, lastAt }
 *
 * `tone`: 'none' (nobody looked) · 'queued' · 'tried' (looked, no answer) ·
 *         'exhausted' (looked, and there is nothing to find) · 'answered'
 *
 * ⚠️ `tried` and `exhausted` are deliberately different. "We could not find it"
 * invites another go; "this event publishes no result" is terminal and tells a
 * rep to stop spending time and go ask a human at the organisation instead.
 */
export function searchHistory(c, now = new Date()) {
  const tag = S(c?.championSearchTag).toLowerCase()
  const attempts = Number(c?.championSearchAttempts) || 0
  const lastAt = S(c?.championSearchedAt)
  const when = whenPhrase(lastAt, now)
  const times = attempts === 1 ? 'once' : `${attempts} times`

  /* A crowned record is not in the hunt. Stated rather than left to the caller
   * so no surface can render "nobody has looked" over an answered record. */
  if (S(c?.champion) || S(c?.winningSchool)) {
    return {
      key: 'answered', tone: 'answered', attempts, lastAt,
      headline: 'A champion is already recorded.',
      detail: when ? `Last searched ${when}.` : '',
    }
  }

  if (tag === 'no_source_exists') {
    return {
      key: 'no_source', tone: 'exhausted', attempts, lastAt,
      headline: `Searched${attempts ? ` ${times}` : ''}${when ? `, last ${when}` : ''} — and there is no published result.`,
      detail: 'Somebody looked and concluded this event does not publish a winner anywhere findable. That is a fact about the event, not a gap in our effort. Getting this one means asking the organisation directly — searching again will not help.',
    }
  }

  if (tag === 'not_found_human' || (attempts > 0 && !tag)) {
    return {
      key: 'not_found', tone: 'tried', attempts, lastAt,
      headline: `Searched ${attempts ? times : 'already'}${when ? `, last ${when}` : ''} — no source named a champion.`,
      detail: attempts > 2
        ? 'Repeated failures usually mean the result is not published where search can reach it — a governing body that posts to PDF or social only. Worth trying the organisation directly rather than a fourth search.'
        : 'The event may have concluded without a published recap yet. Worth another look, or check the governing body directly.',
    }
  }

  if (tag === 'not_yet_occurred') {
    return {
      key: 'not_yet', tone: 'exhausted', attempts, lastAt,
      headline: `Checked${when ? ` ${when}` : ''} — this had not been decided yet.`,
      detail: 'Nothing to find until it is played. If the date on the record has passed, the date is the thing to fix.',
    }
  }

  if (tag === 'split_into_divisions') {
    return {
      key: 'split', tone: 'answered', attempts, lastAt,
      headline: 'This was split into divisions — the champions are on those records.',
      detail: '',
    }
  }

  if (tag === 'requested') {
    return {
      key: 'requested', tone: 'queued', attempts, lastAt,
      headline: `Hunting this one${when ? ` — asked ${when}` : ''}.`,
      detail: 'The hunter runs every 15 minutes and takes requested records first, so this gets looked at shortly. You do not have to wait — the search below is composed for this record.',
    }
  }

  /* ⚠️ THE DEFAULT IS THE HONEST ONE, AND IT IS NOT "not found". Nothing has
   * been tried. Saying so is the difference between a queue position and a
   * dead end, and the old panel showed the same blank form for both. */
  return {
    key: 'never', tone: 'none', attempts: 0, lastAt: '',
    headline: 'Nobody has searched this one yet — the hunter is on it now.',
    detail: 'Pressing this handed the record to the hunter, which runs every 15 minutes and takes requested records first. Come back and the answer will be on the row. You do not have to wait: the search below is already composed for this record.',
  }
}
